import {
  createRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { rootRoute } from './__root'
import {
  useLayouts,
  useCreateLayout,
  useDeleteLayout,
  useRenameLayout,
} from '@/hooks/useLayout'
import { apiClient } from '@/lib/api'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { ShareLayoutDialog } from '@/components/panels/ShareLayoutDialog'
import { UpgradeDialog } from '@/components/panels/UpgradeDialog'
import { ConfirmDialog } from '@/components/panels/ConfirmDialog'
import { useAuth } from '@/hooks/useAuth'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { usePlatform } from '@/hooks/usePlatform'
import { timeAgo } from '@/lib/format'

export const bussboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bussboard',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: LayoutsPage,
})

interface LayoutCardData {
  id: string
  name: string
  description?: string
  updatedAt: string
  deviceCount?: number
  connectionCount?: number
  devices?: unknown[]
  connections?: unknown[]
  role?: 'owner' | 'editor' | 'viewer'
  ownerId?: string
  ownerEmail?: string
  version?: number
}

function LayoutsPage() {
  const { data: layouts, isLoading } = useLayouts()
  const { user } = useAuth()
  // DEV tier override flows through here so layout-creation caps on the
  // page reflect the chosen tier in-session. Real billing decisions
  // still read `user.tier` via UpgradeDialog.
  const { tier: effectiveTier } = useEffectiveFeatures()
  const platform = usePlatform()
  const navigate = useNavigate()
  const createMutation = useCreateLayout()
  const deleteMutation = useDeleteLayout()
  const [shareTarget, setShareTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const currentTier = effectiveTier ?? user?.tier ?? 'free'

  const { owned, shared } = useMemo(() => {
    // The API can briefly return non-array data during SPA fallback or
    // an unauthenticated pre-redirect window; guard before `.filter`.
    const typed = Array.isArray(layouts) ? (layouts as LayoutCardData[]) : []
    return {
      owned: typed
        .filter(l => (l.role ?? 'owner') === 'owner')
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        ),
      shared: typed
        .filter(l => l.role && l.role !== 'owner')
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        ),
    }
  }, [layouts])

  const generateLayoutName = useCallback(() => {
    const existingNames = new Set(owned.map(l => l.name))
    if (!existingNames.has('Untitled Environment'))
      return 'Untitled Environment'
    let i = 2
    while (existingNames.has(`Untitled Environment ${i}`)) i++
    return `Untitled Environment ${i}`
  }, [owned])

  const handleCreateLayout = useCallback(() => {
    setCreateError(null)
    const name = generateLayoutName()
    createMutation.mutate(
      { name },
      {
        onSuccess: data => {
          navigate({ to: '/bussboard/$id', params: { id: data.id } })
        },
        onError: err => {
          const msg = err instanceof Error ? err.message : 'Creation failed'
          if (msg.toLowerCase().includes('limit')) {
            setCreateError(
              `You've reached your ${currentTier} plan environment limit. Upgrade for more environments.`,
            )
          } else {
            setCreateError(msg)
          }
        },
      },
    )
  }, [createMutation, generateLayoutName, navigate, currentTier])

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader
        title="Bussboard"
        workspaceSwitcher="bussboard"
        rightExtras={
          <button
            onClick={handleCreateLayout}
            disabled={createMutation.isPending}
            className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-50 px-2 py-1 rounded hover:bg-accent-muted"
          >
            {createMutation.isPending ? 'Creating\u2026' : '+ New Environment'}
          </button>
        }
      />

      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4'
            : 'max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8'
        }
      >
        {createError && (
          <div className="mb-6 p-4 rounded-xl bg-destructive/5 border border-destructive/20 flex items-center justify-between gap-4">
            <p className="text-sm text-destructive">{createError}</p>
            <div className="flex gap-2 flex-shrink-0">
              {createError.includes('Upgrade') && (
                <button
                  onClick={() => {
                    setCreateError(null)
                    setUpgradeOpen(true)
                  }}
                  className="btn-primary btn-sm"
                >
                  Upgrade
                </button>
              )}
              <button
                onClick={() => setCreateError(null)}
                className="btn-secondary btn-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
          </div>
        ) : Array.isArray(layouts) && layouts.length > 0 ? (
          <div className="space-y-10">
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary mb-3">
                My environments ({owned.length})
              </h2>
              {owned.length === 0 ? (
                <p className="text-sm text-content-tertiary italic">
                  You don&apos;t own any environments yet.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {owned.map(layout => (
                    <LayoutCard
                      key={layout.id}
                      layout={layout}
                      canDelete
                      canRename
                      onDelete={() =>
                        setDeleteTarget({ id: layout.id, name: layout.name })
                      }
                      onShare={() =>
                        setShareTarget({ id: layout.id, name: layout.name })
                      }
                      deletePending={deleteMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </section>

            {shared.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary mb-3">
                  Shared with me ({shared.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {shared.map(layout => (
                    <LayoutCard key={layout.id} layout={layout} />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <EmptyState
            onCreateLayout={handleCreateLayout}
            isCreating={createMutation.isPending}
            currentTier={currentTier}
          />
        )}
      </main>

      <ShareLayoutDialog
        key={`share-${shareTarget?.id ?? 'none'}`}
        isOpen={shareTarget !== null}
        onClose={() => setShareTarget(null)}
        layoutId={shareTarget?.id ?? null}
        layoutName={shareTarget?.name ?? ''}
      />
      <UpgradeDialog
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return
          deleteMutation.mutate(deleteTarget.id)
          setDeleteTarget(null)
        }}
        title={
          deleteTarget
            ? `Delete "${deleteTarget.name}"?`
            : 'Delete environment?'
        }
        description="This removes every device and connection in the environment and can't be undone."
        confirmLabel="Delete"
        destructive
        pending={deleteMutation.isPending}
      />
    </div>
  )
}

function EmptyState({
  onCreateLayout,
  isCreating,
  currentTier,
}: {
  onCreateLayout: () => void
  isCreating: boolean
  currentTier: string
}) {
  const limitLabel =
    currentTier === 'free'
      ? '1 environment with up to 20 devices'
      : currentTier === 'pro'
        ? '2 environments with up to 100 devices each'
        : '20 environments with up to 500 devices each'

  return (
    <div className="text-center py-16 sm:py-24 max-w-md mx-auto">
      <div className="mx-auto w-20 h-20 rounded-2xl bg-accent-muted flex items-center justify-center mb-6">
        <svg
          className="w-10 h-10 text-accent"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.886-3.497 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-content-primary mb-3">
        Create your first Environment
      </h2>
      <p className="text-content-secondary mb-2 leading-relaxed">
        Map any signal-flow rig — studio, live stage, broadcast truck, camera +
        lighting setup. Place devices on a canvas, wire connections between
        ports, and organize everything into zones.
      </p>
      <p className="text-xs text-content-tertiary mb-8">
        Your plan includes {limitLabel}.
      </p>
      <button
        onClick={onCreateLayout}
        disabled={isCreating}
        className="btn-primary btn-lg rounded-xl font-semibold text-lg shadow-medium disabled:opacity-50"
      >
        {isCreating ? 'Creating\u2026' : 'Create Environment'}
      </button>
    </div>
  )
}

interface LayoutCardProps {
  layout: LayoutCardData
  canDelete?: boolean
  canRename?: boolean
  onDelete?: () => void
  onShare?: () => void
  deletePending?: boolean
}

function LayoutCard({
  layout,
  canDelete,
  canRename,
  onDelete,
  onShare,
  deletePending,
}: LayoutCardProps) {
  const role = layout.role ?? 'owner'
  const deviceCount = layout.deviceCount ?? layout.devices?.length ?? 0
  const connectionCount =
    layout.connectionCount ?? layout.connections?.length ?? 0

  const renameMutation = useRenameLayout()
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState(layout.name)

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim() || newName.trim() === layout.name) {
      setRenaming(false)
      return
    }
    try {
      await renameMutation.mutateAsync({
        id: layout.id,
        name: newName.trim(),
        version: layout.version ?? 0,
      })
      setRenaming(false)
    } catch {
      setRenaming(false)
    }
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low overflow-hidden border border-brd-subtle card-interactive group">
      <Link
        to="/bussboard/$id"
        params={{ id: layout.id }}
        className="block"
        tabIndex={-1}
      >
        <div className="h-28 bg-gradient-to-br from-accent to-second flex items-center justify-center relative">
          <div className="text-white text-4xl opacity-30">&#x266B;</div>
          {role !== 'owner' && (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/40 text-[10px] uppercase tracking-wider font-semibold text-white">
              Shared &middot; {role}
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-150 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-white text-sm font-medium bg-black/40 px-3 py-1 rounded-full">
              Open
            </span>
          </div>
        </div>
      </Link>
      <div className="p-4">
        {renaming ? (
          <form onSubmit={handleRename} className="mb-3">
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="w-full px-2 py-1 rounded-md border border-brd-emphasis bg-surface-sunken text-content-primary text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-accent/50"
              autoFocus
              onBlur={handleRename}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setNewName(layout.name)
                  setRenaming(false)
                }
              }}
            />
          </form>
        ) : (
          <h3
            className={`text-lg font-semibold text-content-primary mb-1 ${canRename ? 'cursor-pointer hover:text-accent' : ''}`}
            onClick={
              canRename
                ? e => {
                    e.stopPropagation()
                    setNewName(layout.name)
                    setRenaming(true)
                  }
                : undefined
            }
            title={canRename ? 'Click to rename' : undefined}
          >
            {layout.name}
          </h3>
        )}
        {role !== 'owner' && layout.ownerEmail && (
          <p className="text-xs text-content-tertiary mb-1">
            Shared by {layout.ownerEmail}
          </p>
        )}
        {layout.description && (
          <p className="text-sm text-content-secondary mb-3">
            {layout.description}
          </p>
        )}
        <div className="text-xs text-content-tertiary mb-4 flex items-center gap-3">
          <span>
            {deviceCount} device{deviceCount !== 1 ? 's' : ''}
          </span>
          <span className="text-brd-emphasis">&middot;</span>
          <span>
            {connectionCount} connection{connectionCount !== 1 ? 's' : ''}
          </span>
          <span className="text-brd-emphasis">&middot;</span>
          <span title={new Date(layout.updatedAt).toLocaleString()}>
            {timeAgo(layout.updatedAt)}
          </span>
        </div>
        <div className="flex gap-2">
          <Link
            to="/bussboard/$id"
            params={{ id: layout.id }}
            className="flex-1 btn-primary btn-sm text-center"
          >
            Open
          </Link>
          {onShare && (
            <button
              onClick={onShare}
              className="btn-secondary btn-sm"
              title="Share this environment with another account"
            >
              Share
            </button>
          )}
          {canDelete && onDelete && (
            <button
              onClick={onDelete}
              disabled={deletePending}
              className="btn btn-sm bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
